import { useMemo } from "react";

import { isHTMLString } from "../helpers/helpers";
import appSettings from "../settings/appSettings";
import ErrorInfoBaseComponent from "./ErrorInfoBaseComponent";

const { wvusLogger } = appSettings;

/**
 * @description ErrorInfo component displays error information based on the provided error key.
 * It dynamically overrides the AlertMessage and ContentMessage props with values from the application settings,
 * if they are not already provided and if the corresponding configuration values are valid HTML strings.
 * @param {object} props - The component props.
 * @param {string} props.errorKey - The key to determine which error message to display.
 * @param {React.ReactNode} [props.AlertMessage] - The alert message to display. If not provided, it will be dynamically set from the configuration.
 * @param {React.ReactNode} [props.ContentMessage] - The content message to display. If not provided, it will be dynamically set from the configuration.
 * @param {object} [props.restProps] - Other props to pass to the ErrorInfoBaseComponent.
 * @returns {JSX.Element} The ErrorInfo component.
 */
const ErrorInfo = (props) => {
  const { errorKey, ...restProps } = props;
  const { config = {} } = appSettings;

  const overrideProps = useMemo(() => {
    const widgetConfig = config?.widgetConfig || {};
    const errorKeyMap = {
      general: {
        AlertMessage: "errorAlertMessageGeneral",
        ContentMessage: "errorContentMessageGeneral"
      },
      noConfig: {
        AlertMessage: "errorAlertMessageNoConfig",
        ContentMessage: "errorContentMessageNoConfig"
      },
      noHandShake: {
        AlertMessage: "errorAlertMessageNoHandShake",
        ContentMessage: "errorContentMessageNoHandShake"
      },
      registerUser: {
        AlertMessage: "errorAlertMessageRegisterUser",
        ContentMessage: "errorContentMessageRegisterUser"
      },
      sendAuthLink: {
        AlertMessage: "errorAlertMessageSendAuthLink",
        ContentMessage: "errorContentMessageSendAuthLink"
      },
      communityHubInterest: {
        AlertMessage: "errorAlertMessageCommunityHubInterest",
        ContentMessage: "errorContentMessageCommunityHubInterest"
      }
    };

    const isValidErrorKey = errorKey && errorKeyMap[errorKey];

    if (!isValidErrorKey) {
      return {};
    }

    return Object.entries(errorKeyMap[errorKey]).reduce((dynProps, [propKey, configKey]) => {
      // If Prop Is Already Defined or Config Is Properly Defined, Return DynProps
      if (
        restProps[propKey] ||
        !widgetConfig[configKey] ||
        typeof widgetConfig[configKey] !== "string"
      ) {
        return dynProps;
      }

      if (!isHTMLString(widgetConfig[configKey])) {
        wvusLogger.log(
          {
            errorMessage: `Invalid HTML string for ${configKey} in widgetConfig`,
            errorContext: "ErrorInfo component"
          },
          wvusLogger.getLogLevelNames().ERROR
        );
        console.error(`Invalid HTML string for ${configKey} in widgetConfig`);

        return dynProps;
      }

      dynProps[propKey] = () => (
        <div
          style={{ display: "inline" }}
          dangerouslySetInnerHTML={{ __html: widgetConfig[configKey] }}></div>
      );

      return dynProps;
    }, {});
  }, [errorKey, restProps, config]);

  const propAlertMessage = restProps?.AlertMessage ?? overrideProps?.AlertMessage;
  const propContentMessage = restProps?.ContentMessage ?? overrideProps?.ContentMessage;

  return (
    <ErrorInfoBaseComponent
      {...restProps}
      AlertMessage={propAlertMessage}
      ContentMessage={propContentMessage}
    />
  );
};

export default ErrorInfo;
